<template>
  <div class="pricing-content section-padding">
    <div class="container text-center">
      <template v-if="prices && prices.length">
        <div v-if="visibleSeasons.length" ref="pricingSwiperRoot" class="pricing-swiper">
          <div class="swiper-container">
            <div class="swiper-wrapper">
              <div
                v-for="season in visibleSeasons"
                :key="season.key"
                class="swiper-slide"
              >
                <PricingSeasonCard :season="season" />
              </div>
            </div>
          </div>
          <div v-if="visibleSeasons.length > 1" class="text-center">
            <div class="swiper-pagination top40"></div>
          </div>
        </div>

        <div class="row" style="margin-top: 20px" v-if="currentPrice && hasDisplayText(currentPrice.season_hint)">
          <p v-html="currentPrice.season_hint"></p>
        </div>
      </template>

      <div v-else class="row">
        <p>Preise werden geladen...</p>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { hasDisplayText } from '../../lib/helper.js'
import { SWIPER_MOBILE_BREAKPOINTS } from '../../lib/swiperInit.js'
import { useJQuerySwiper, useJQuerySwiperWatch } from '../../composables/useJQuerySwiper.js'
import { equalizePricingHeights } from '../../lib/pricingHeights.js'
import PricingSeasonCard from '../misc/PricingSeasonCard.vue'

const SEASON_DEFINITIONS = [
  {
    key: 'season_1',
    nameKey: 'season_1_name',
    priceKey: 'season_1_price',
    dateKey: 'season_1_date',
    subline1Key: 'season_1_subline1',
    subline2Key: 'season_1_subline2',
    active: false
  },
  {
    key: 'season_2',
    nameKey: 'season_2_name',
    priceKey: 'season_2_price',
    dateKey: 'season_2_date',
    subline1Key: 'season_2_subline1',
    subline2Key: 'season_2_subline2',
    active: true
  },
  {
    key: 'season_3',
    nameKey: 'season_3_name',
    priceKey: 'season_3_price',
    dateKey: 'season_3_date',
    textKey: 'season_3_text',
    subline1Key: 'season_3_subline1',
    subline2Key: 'season_3_subline2',
    active: false,
    showContactWhenText: true
  }
]

export default {
  components: {
    PricingSeasonCard
  },
  props: {
    apartment: Object,
    prices: Array
  },
  setup(props) {
    const pricingSwiperRoot = ref(null)

    const currentPrice = computed(() => {
      if (!props.prices || props.prices.length === 0) return null

      if (props.apartment && props.apartment.slug) {
        const priceForApartment = props.prices.find(
          (price) => price.apartment && price.apartment.slug === props.apartment.slug
        )
        if (priceForApartment) return priceForApartment
      }

      return props.prices[0]
    })

    const visibleSeasons = computed(() => {
      if (!currentPrice.value) return []

      return SEASON_DEFINITIONS.filter((def) =>
        hasDisplayText(currentPrice.value[def.nameKey])
      ).map((def) => ({
        key: def.key,
        name: String(currentPrice.value[def.nameKey]).trim(),
        price: currentPrice.value[def.priceKey],
        date: currentPrice.value[def.dateKey],
        subline1: currentPrice.value[def.subline1Key],
        subline2: currentPrice.value[def.subline2Key],
        text: def.textKey ? currentPrice.value[def.textKey] : null,
        showContact:
          def.showContactWhenText &&
          hasDisplayText(def.textKey ? currentPrice.value[def.textKey] : null),
        active: def.active
      }))
    })

    const desktopSlidesPerView = () => {
      const count = visibleSeasons.value.length
      if (count <= 1) return 1
      if (count === 2) return 2
      return 3
    }

    const equalizeCurrentPricingHeights = () => {
      equalizePricingHeights(window.$(pricingSwiperRoot.value))
    }

    const { scheduleInit } = useJQuerySwiper({
      rootRef: pricingSwiperRoot,
      canInit: () => visibleSeasons.value.length > 0,
      buildOptions: () => ({
        slidesPerView: desktopSlidesPerView(),
        spaceBetween: 20,
        breakpoints: SWIPER_MOBILE_BREAKPOINTS,
        onInit() {
          equalizeCurrentPricingHeights()
        },
        onResize() {
          equalizeCurrentPricingHeights()
        }
      }),
      onDestroy: ($root) => {
        $root.find('.pricing_item').matchHeight('remove')
      }
    })

    useJQuerySwiperWatch(
      [() => props.prices, () => props.apartment, visibleSeasons],
      scheduleInit
    )

    let resizeTimer = null
    const onWindowResize = () => {
      clearTimeout(resizeTimer)
      resizeTimer = setTimeout(equalizeCurrentPricingHeights, 100)
    }

    onMounted(() => {
      window.addEventListener('resize', onWindowResize)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', onWindowResize)
      clearTimeout(resizeTimer)
    })

    return {
      pricingSwiperRoot,
      currentPrice,
      visibleSeasons,
      hasDisplayText
    }
  }
}
</script>

<style scoped>
.section-padding {
  padding-bottom: 72px;
}

.pricing-swiper :deep(.swiper-container) {
  overflow: hidden;
  touch-action: pan-y;
}

@media (max-width: 767px) {
  .section-padding {
    padding-bottom: 1.5rem;
  }
}
</style>
